import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

interface SegmentedControlProps {
  tabs: string[]
  selectedTab: string
  onTabChange: (tab: string) => void
  style?: React.CSSProperties
  /** Stretch across the container, every segment the same width. */
  fill?: boolean
}

const ControlWrapper = styled.div<{ $fill?: boolean }>`
  display: ${({ $fill }) => $fill ? 'flex' : 'inline-flex'};
  flex-wrap: ${({ $fill }) => $fill ? 'wrap' : 'nowrap'};
  width: ${({ $fill }) => $fill ? '100%' : 'auto'};
  box-sizing: border-box;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 11px;
  background: var(--surface-hover, #f7f9fc);
  position: relative;
  z-index: 1;
`

const SegmentButton = styled.button<{ $active?: boolean, $fill?: boolean }>`
  ${({ $fill }) => $fill && `
    flex: 1 1 auto;
    min-width: max-content;
    text-align: center;
    white-space: nowrap;
  `}
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: ${props => props.$active ? 'var(--ink-strong, #10141f)' : 'var(--text-muted, #6f7686)'};
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1;
  padding: ${({ $fill }) => $fill ? '8px 10px' : '8px 16px'};
  transition: color 0.16s ease, transform 0.1s ease;
  user-select: none;
  position: relative;
  z-index: 1;

  &:hover {
    color: var(--ink-strong, #10141f);
  }

  &:active {
    transform: scale(0.97);
  }
`

const SlidingHighlight = styled.div<{ $left: number, $top: number, $width: number, $height: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;
  transform: translate(${({ $left }) => $left}px, ${({ $top }) => $top}px);
  background: var(--surface, #ffffff);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), width 0.22s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`

export const SegmentedControl = ({
  tabs,
  selectedTab,
  onTabChange,
  style,
  fill
}: SegmentedControlProps): React.ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
  const [highlightStyle, setHighlightStyle] = useState({ left: 0, top: 0, width: 0, height: 0 })

  useEffect(() => {
    const measure = (): void => {
      const activeEl = tabRefs.current[selectedTab]
      if (activeEl) {
        setHighlightStyle({
          left: activeEl.offsetLeft,
          top: activeEl.offsetTop,
          width: activeEl.offsetWidth,
          height: activeEl.offsetHeight
        })
      }
    }
    measure()
    // Segments that share the width move when the container does.
    if (!fill || typeof ResizeObserver === 'undefined' || !wrapperRef.current) return
    const observer = new ResizeObserver(measure)
    observer.observe(wrapperRef.current)
    return () => observer.disconnect()
  }, [selectedTab, tabs, fill])

  return (
    <ControlWrapper ref={wrapperRef} style={style} $fill={fill}>
      <SlidingHighlight
        $left={highlightStyle.left}
        $top={highlightStyle.top}
        $width={highlightStyle.width}
        $height={highlightStyle.height} />
      {tabs.map(tab => (
        <SegmentButton
          key={tab}
          ref={el => (tabRefs.current[tab] = el)}
          type="button"
          $active={tab === selectedTab}
          $fill={fill}
          onClick={() => onTabChange(tab)}
        >
          {tab}
        </SegmentButton>
      ))}
    </ControlWrapper>
  )
}

export default SegmentedControl
